import * as _ from "underscore";
import moment = require('moment');
import { ResidentFinanceFees, Resident, ResidentContact, ResidentFinanceEffectiveDate } from '../Models';
import { IResidentsService } from '../services/IResidentsService';
import type { IStateService } from "angular-ui-router";

interface IAddEditFeeEffectiveDatesDialogConfig {
    effectiveDateType: string;
    effectiveDates: ResidentFinanceEffectiveDate[];
    callBack: any;
    isAdmitted: boolean;
    admissionDate: Date;
    isEmptyCollectionValid: boolean;
    isFirstValueBasedOnAdmissionDate: boolean;
    isIncludeResidentName: boolean;
    residentName: string;
    lifeTimeCap: number;
    lifeTimeCapEndDate: any;
    showAgreedMpirFields?: boolean;
    removeFirstRowAgreedMPIR: boolean;
    agreedMpirPersistence?: {
        useAgreedMPIR: boolean;
        agreedMPIR: number | null;
        selectedHistoricalDate: any;
    };
    initialItemCount: number;
    careFundingHocSupplementRates: CareFundingRateForPeriod[];
}

enum EffectiveDateInvalidReason {
    None = 0,
    NonUnique = 1,
    MultipleAdmissionDate = 2,
    NCC = 3,
    Amount = 4,
    HOCAmount = 5
}

class EffectiveDateItem {

    date: Date;
    value: any;
    isValid: boolean;
    invalidReason: EffectiveDateInvalidReason;
    isEditable: boolean;
    hocEffectiveDate: Date;

    constructor(date: Date) {
        this.date = date ? moment(date).toDate() : null;
        this.value = null;
        this.isValid = true;
        this.isEditable = true;
        this.invalidReason = EffectiveDateInvalidReason.None;
        this.hocEffectiveDate = moment(date).toDate();
    }
}

abstract class EffectiveDatesDialog {
    config: IAddEditFeeEffectiveDatesDialogConfig;
    heading: string;
    label: string;
    dateLabel: string;
    minAmount: number;
    maxAmount: number;
    isncc: boolean;
    ishoc: boolean;
    isHocAmountExceed: boolean;
    isHocAmountValid: boolean;
    isBillingIntegratedFacilities: boolean;
    maxPaymentDate: string;
    minPaymentDate: Date;
    type: string;
    items: EffectiveDateItem[];
    admissionDateItem: EffectiveDateItem;
    residentName: string;
    agreedMpirPersistence: any;
    selectedHistoricalDate: any;
    agreedMPIR: number | null = null;
    useAgreedMPIR: boolean = false;
    initialItemCount: number;
    protected constructor(
        config: IAddEditFeeEffectiveDatesDialogConfig, valueType: string
    ) {
        this.config = config;
        this.type = valueType;
        this.setMpirData();
        this.setItems(config.effectiveDates);
        this.initialItemCount = this.items.length;
        if (this.config.isAdmitted) {
            this.addItem();
        }
    }

    addItem() {
        this.items.unshift(new EffectiveDateItem(null));
    }

    removeItem(index) {
        this.items.splice(index, 1);
    }

    setItems(items: any[]) {
        this.items = [];
        _.each(items, item => {
            let effectiveDate = new EffectiveDateItem(item.effectiveFromDate);
            effectiveDate.value = this.getItemValue(this.type, item);
            this.items.push(effectiveDate);
        }, this);

        this.items = _.sortBy(this.items, item => item.date).reverse();

        // Set the last item as the admissionDate and make it non-editable
        if (this.config.isFirstValueBasedOnAdmissionDate && this.items.length > 0) {
            this.admissionDateItem = this.items[this.items.length - 1];
            this.admissionDateItem.isEditable = false;
            if (this.config.effectiveDateType == "HotellingContributionFee") {
                const hocResult = this.items.find(item => {
                    const result = this.isHocAmountExceeded(item.date, item.value);
                    return result.exceeded;
                });

                if (hocResult) {
                    this.admissionDateItem.isValid = false;
                    this.admissionDateItem.hocEffectiveDate = hocResult.date;
                }
            }

        }
    }

    setMpirData(): void {
        if (this.config.showAgreedMpirFields && this.config.agreedMpirPersistence) {

            const persistence = this.config.agreedMpirPersistence;

            this.useAgreedMPIR = persistence.useAgreedMPIR ?? false;
            this.agreedMPIR = persistence.agreedMPIR ?? null;
            this.selectedHistoricalDate = persistence.selectedHistoricalDate
                ? persistence.selectedHistoricalDate.trim()
                : null;
        } else if (this.config.showAgreedMpirFields) {
            this.useAgreedMPIR = false;
            this.agreedMPIR = null;
            this.selectedHistoricalDate = null;
        }
    }

    abstract getValue(type, item);

    abstract getItemValue(type, item);


    getValidItems(): ResidentFinanceEffectiveDate[] {
        return this
            .items
            .filter(effectiveDate => {
                return effectiveDate.date !== null && effectiveDate.value !== null;
            })
            .map(item => {
                let effectiveDate = new ResidentFinanceEffectiveDate();
                effectiveDate.effectiveDateType = this.config.effectiveDateType;
                effectiveDate.effectiveFromDate = item.date;
                effectiveDate.value = this.getValue(this.type, item);
                return effectiveDate;
            });
    }

    onDateChange(currentEffectiveDate: EffectiveDateItem): void {
        currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.None;
        currentEffectiveDate.isValid = true;
        if (!currentEffectiveDate.date) {
            return;
        }
        if (this.admissionDateItem != undefined && this.items.indexOf(currentEffectiveDate) != this.items.indexOf(this.admissionDateItem)) {
            let isBeforeAdmissionDate = moment(currentEffectiveDate.date).isAfter(moment(this.admissionDateItem.date));
            if (!isBeforeAdmissionDate) {
                currentEffectiveDate.isValid = false;
                currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.MultipleAdmissionDate;
            } else if (this.isncc && this.isBillingIntegratedFacilities) {
                let today = new Date();
                let checkTodays = moment(today).isBefore(moment(this.config.lifeTimeCapEndDate));
                let checkeffectiveDate = moment(currentEffectiveDate.date).isAfter(moment(this.config.lifeTimeCapEndDate));
                if (checkTodays && checkeffectiveDate) {
                    currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.NCC;
                    currentEffectiveDate.isValid = false;
                }
            } else if (this.ishoc) {
                // Reset defaults
                currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.None;
                this.isHocAmountExceed = false;
                this.admissionDateItem.isValid = true;
                this.isHocAmountValid = true;

                // Check current effective date
                const hocResult = this.isHocAmountExceeded(currentEffectiveDate.date, currentEffectiveDate.value);
                if (hocResult.exceeded) {
                    currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.HOCAmount;
                    this.isHocAmountValid = false;
                }

                // Check all items
                const hocItem = this.items.find(item => {
                    const result = this.isHocAmountExceeded(item.date, item.value);
                    return result.exceeded;
                });

                if (hocItem) {
                    this.isHocAmountExceed = true;
                    // Only mark invalid if reason is NOT 5 
                    if (hocItem.invalidReason !== 5) {
                        this.admissionDateItem.isValid = false;
                        this.admissionDateItem.hocEffectiveDate = hocItem.date;
                    }
                }
            }
        }
        if (currentEffectiveDate.isValid) {
            const count = _.filter(this.items,
                item => {
                    return moment(item.date).isSame(moment(currentEffectiveDate.date), 'day');
                }).length;

            if (count !== 1) {
                currentEffectiveDate.isValid = false;
                currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.NonUnique;
            }
        }
    }

    onAmountChange(currentEffectiveDate: EffectiveDateItem): void {
        if (this.isncc && this.isBillingIntegratedFacilities) {
            currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.None;
            if (currentEffectiveDate.value === undefined && currentEffectiveDate.value !== null) {
                currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.Amount;
            }
        }
        if (this.ishoc) {
            // Reset defaults
            currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.None;
            this.isHocAmountExceed = false;
            this.admissionDateItem.isValid = true;
            this.isHocAmountValid = true;

            // Check current effective date
            const hocResult = this.isHocAmountExceeded(currentEffectiveDate.date, currentEffectiveDate.value);
            if (hocResult.exceeded) {
                currentEffectiveDate.invalidReason = EffectiveDateInvalidReason.HOCAmount;
                this.isHocAmountValid = false;
            }

            // Check all items
            const hocItem = this.items.find(item => {
                const result = this.isHocAmountExceeded(item.date, item.value);
                return result.exceeded;
            });

            if (hocItem) {
                this.isHocAmountExceed = true;
                // Only mark invalid if reason is NOT 5 
                if (hocItem.invalidReason !== 5) {
                    this.admissionDateItem.isValid = false;
                    this.admissionDateItem.hocEffectiveDate = hocItem.date;
                }
            }
        }

    }

    private isHocAmountExceeded(date: any, value: number): { exceeded: boolean, affectedDate?: moment.Moment } {
        if (!date || value === undefined) {
            return { exceeded: false };
        }
        const admissionDate = moment(date);
        const hocSupplementRate = this.config.careFundingHocSupplementRates.find(rate => {
            const startDate = moment(rate.startDateUtc);
            const endDate = rate.endDateUtc ? moment(rate.endDateUtc) : null;
            return startDate.isSameOrBefore(admissionDate)
                && (!endDate || endDate.isSameOrAfter(admissionDate));
        });
        return {
            exceeded: hocSupplementRate != null && hocSupplementRate.rate < value,
            affectedDate: hocSupplementRate ? admissionDate : undefined
        };
    }


    hasAtLeastOneValidDate(): boolean {
        const now = moment().startOf('day').utc();
        return this
            .items
            .filter(effectiveDate => {
                return effectiveDate.date !== null && effectiveDate.value !== null && moment(effectiveDate.date).isSameOrBefore(now, 'day');
            })
            .length > 0;
    }

    allDatesAreUnique(): boolean {
        const numberOfDatesEntered = this.items.filter(item => {
            return item.date !== null && item.value !== null;
        }).length;
        const numberOfUniqueDates = this
            .items
            .filter(item => item.date !== null)
            .map(item => moment(item.date).toISOString())
            .filter((date, i, list) => list.indexOf(date) === i).length;
        return numberOfUniqueDates === numberOfDatesEntered;
    }

    hasOnlyOneEffectiveDateAsAdmissionDate(): boolean {
        const lastItemIndex = this.items.length - 1;
        let isValid = true;

        const countOfInvalidDates = this.items.filter((item, index) => {
            if (index == lastItemIndex) {
                return false;
            }
            if (item.date === null) {
                return false;
            }
            if (index < lastItemIndex) {
                return !moment(item.date).isAfter(moment(this.admissionDateItem.date));
            }
        }).length;

        return countOfInvalidDates === 0;
    }


    protected allItemsAreValid(): boolean {

        // if the effective date list is empty and pre condition is the list can be started as empty , still list is valid
        if (this.config.isEmptyCollectionValid && this.items.length == 0) {
            return true;
        }
        if (this.ishoc && this.isHocAmountExceed) {
            return false;
        }
        return this
            .items
            .every(effectiveDate => {
                return (effectiveDate.date === null && effectiveDate.value === null) ||
                    (effectiveDate.date !== null && effectiveDate.value !== null);
            }) &&
            this.items.every(effectiveDate => effectiveDate.isValid) &&
            this.hasAtLeastOneValidDate() &&
            this.allDatesAreUnique() &&
            (!this.config.isFirstValueBasedOnAdmissionDate || this.hasOnlyOneEffectiveDateAsAdmissionDate());
    }

    protected allItemsAreValidAgreedMPIR(): boolean {

        if (this.items.length === 0) {
            return true;
        }

        if (this.items.length > this.initialItemCount) {

            if (this.useAgreedMPIR === true) {
                this.useAgreedMPIR = false;
                this.agreedMPIR = null;
                this.selectedHistoricalDate = null;
            }

            if (this.config.showAgreedMpirFields && this.config.agreedMpirPersistence && this.config.removeFirstRowAgreedMPIR) {

                const persistence = this.config.agreedMpirPersistence;

                this.useAgreedMPIR = persistence.useAgreedMPIR ?? false;
                this.agreedMPIR = persistence.agreedMPIR ?? null;
                this.selectedHistoricalDate = persistence.selectedHistoricalDate
                    ? persistence.selectedHistoricalDate.trim()
                    : null;

                { this.removeItem(0); this.config.removeFirstRowAgreedMPIR = false; this.config.agreedMpirPersistence = null; }

                return true;
            }

            return false;
        }

        if (!this.allItemsAreValid()) {

            if (this.useAgreedMPIR === true) {
                this.useAgreedMPIR = false;
                this.agreedMPIR = null;
                this.selectedHistoricalDate = null;
            }
            return false;
        }

        return true;
    }

}

class AmountEffectiveDatesDialog extends EffectiveDatesDialog {
    constructor(config: IAddEditFeeEffectiveDatesDialogConfig, dialogHeading: string, dateLabel: string, maxPaymentDate: string, minAmount: number, minPaymentDate: Date, maxAmount: number, isncc: boolean, ishoc: boolean, isHocAmountExceed: boolean, isHocAmountValid: boolean, isBillingIntegratedFacilities: boolean) {
        super(config, 'amount');
        this.dateLabel = dateLabel;
        this.label = 'Amount';
        this.heading = dialogHeading;
        this.maxPaymentDate = maxPaymentDate;
        this.minAmount = minAmount;
        this.maxAmount = maxAmount;
        this.isncc = isncc;
        this.ishoc = ishoc;
        this.isHocAmountValid = isHocAmountValid;
        this.isHocAmountExceed = isHocAmountExceed;
        this.minPaymentDate = minPaymentDate;
        this.isBillingIntegratedFacilities = isBillingIntegratedFacilities;
        this.residentName = config.isIncludeResidentName ? config.residentName : null;
    }

    getValue(type, item): string {
        return type === 'amount' ? String(item.value) : null;
    }

    getItemValue(type, item): number {
        return type === 'amount' ? Number(item.value) : null;
    }
}

class SupportLevelEffectiveDatesDialog extends EffectiveDatesDialog {
    constructor(config: IAddEditFeeEffectiveDatesDialogConfig) {
        super(config, 'support-level');
        this.heading = 'Support status';
        this.label = 'Support Level';
    }

    getValue(type, item): string {
        return type === 'support-level' ? item.value : null;
    }

    getItemValue(type, item): string {
        return type === 'support-level' ? item.value : null;
    }
}

class RADRACEffectiveDatesDialog extends AmountEffectiveDatesDialog {

    constructor(config: IAddEditFeeEffectiveDatesDialogConfig, dialogHeading: string, dateLabel: string, maxPaymentDate: string, minAmount: number, minPaymentDate: Date, maxAmount: number, isncc: boolean, isBillingIntegratedFacilities: boolean) {
        super(config, dialogHeading, dateLabel, maxPaymentDate, minAmount, minPaymentDate, maxAmount, isncc, false, false, true, isBillingIntegratedFacilities);
        this.dateLabel = dateLabel;
        this.label = 'Amount';
        this.heading = dialogHeading;
        this.maxPaymentDate = maxPaymentDate;
        this.minAmount = minAmount;
        this.maxAmount = maxAmount
        this.isncc = isncc;
        this.isBillingIntegratedFacilities = isBillingIntegratedFacilities;
    }

    allItemsAreValid(): boolean {
        return super.allItemsAreValid() && this.isTotalAmountPaidGreaterThanOrEqualToZero()
    }

    isTotalAmountPaidGreaterThanOrEqualToZero(): boolean {
        var total = _.reduce(this.items, (total, item) => {
            if (item.isValid === false) {
                return total;
            }
            return total + (Number(item.value) || 0);
        }, 0);

        if (isNaN(total)) {
            total = 0;
        }
        return total >= 0;
    }
}


class AddEditFeeEffectiveDatesDialogController {
    static $inject = ["$state", "modalConfig", "residents.service", "EffectiveDateTypes", "$rootScope"];

    public dialog: EffectiveDatesDialog;
    public $hide: any;
    public isRadRacDialog: boolean;
    public isBillingIntegratedFacility;
    public maxamount: any;
    public isncc: boolean;
    public isBillingIntegratedFacilities: boolean;
    constructor(
        private $state: IStateService,
        private modalConfig: IAddEditFeeEffectiveDatesDialogConfig,
        private residentsService: IResidentsService,
        private EffectiveDateTypes: any,
        private $rootScope: any
    ) {
        this.isBillingIntegratedFacility = $rootScope.isBillingIntegratedFacility;
        this.isBillingIntegratedFacilities = $rootScope.isBillingIntegratedFacility;
        if (modalConfig.effectiveDateType === EffectiveDateTypes.SupportLevel) {
            this.dialog = new SupportLevelEffectiveDatesDialog(modalConfig);
        }
        else if (modalConfig.effectiveDateType === EffectiveDateTypes.RadPaid ||
            modalConfig.effectiveDateType === EffectiveDateTypes.RacPaid) {
            let heading = '';
            let dateLabel = 'Payment Date';
            let minAmount = -9999999.99;
            let maxAmount = 9999999.99;
            let maxPaymentDate = "today";
            let isncc = false;
            switch (modalConfig.effectiveDateType) {
                case EffectiveDateTypes.RadPaid:
                    heading = 'RAD Payment';
                    break;
                case EffectiveDateTypes.RacPaid:
                    heading = 'RAC Payment';
                    break;
                default:
                    break;
            }
            this.dialog = new RADRACEffectiveDatesDialog(modalConfig, heading, dateLabel, maxPaymentDate, minAmount, modalConfig.admissionDate, maxAmount, isncc, this.isBillingIntegratedFacilities);
            this.isRadRacDialog = true;
        }
        else {
            let heading = '';
            let dateLabel = 'Effective Date';
            let minAmount = 0;
            let maxPaymentDate = '';
            let maxAmount = 9999999.99;
            let isncc = false;
            let ishoc = false;
            switch (modalConfig.effectiveDateType) {
                case EffectiveDateTypes.MeansTestedCareFee:
                    heading = 'Means tested care fee';
                    break;
                case EffectiveDateTypes.Pre2014IncomeTestedFee:
                    heading = 'Income tested care fee';
                    break;
                case EffectiveDateTypes.DailyAccommodationContribution:
                    heading = 'Daily accommodation contribution';
                    break;
                case EffectiveDateTypes.AgreedRoomPrice:
                    heading = 'Agreed room price';
                    break;
                case EffectiveDateTypes.IngoingContribution:
                    heading = 'Ingoing contribution';
                    dateLabel = "Payment Date";
                    maxPaymentDate = "today";
                    break;
                case EffectiveDateTypes.HotellingContributionFee:
                    heading = 'Hotelling contribution';
                    ishoc = true;
                    break;
                case EffectiveDateTypes.NonClinicalCareContributionFee:
                    heading = 'Non-clinical care contribution';
                    maxAmount = 130000 - modalConfig.lifeTimeCap;
                    isncc = true;
                    break;
                default:
                    break;
            }
            this.dialog = new AmountEffectiveDatesDialog(modalConfig, heading, dateLabel, maxPaymentDate, minAmount, null, maxAmount, isncc, ishoc, false, true, this.isBillingIntegratedFacilities);
        }
    }

    public bindHide = (hide) => {
        this.$hide = hide;
    };

    onUseAgreedMpirChange(): void {
        if (this.dialog.useAgreedMPIR === false) {
            this.dialog.agreedMPIR = null;
            this.dialog.selectedHistoricalDate = null;
        }
    }

    public done = (isValid: boolean) => {
        if (isValid) {
            const mpirData = this.modalConfig.showAgreedMpirFields ? {
                useAgreedMPIR: this.dialog.useAgreedMPIR,
                agreedMPIR: this.dialog.agreedMPIR,
                selectedHistoricalDate: this.dialog.selectedHistoricalDate
            } : undefined;

            this.modalConfig.callBack(this.dialog.config.effectiveDateType, this.dialog.getValidItems(), mpirData);
            this.$hide();
        }
    };

    public removeItem(index) {
        this.dialog.removeItem(index);
    }
}

export = AddEditFeeEffectiveDatesDialogController;